import { q } from '@engineer-os/engine';
import { LEVELS_DIR, WORKSPACE_ROOT } from './types.js';
/**
 * A level's work in progress, out of the guest and back into it.
 *
 * The workspace lives in the emulator's memory, so before this a reload, a
 * closed tab, opening another level and every deploy (which is a reload)
 * threw away whatever a learner had done in a level they had not finished.
 * This keeps it.
 *
 * ## What is kept is the difference, never the tree
 *
 * The line into the guest is slow — about 1.2 s per 32 KiB going in — and the
 * level's starting tarball is already in the guest under /levels. So what is
 * saved is what the learner changed: every file or symlink created or
 * modified since the level was seeded, as one gzip tar; every directory
 * created or changed, with its mode; and every path the seed had that is
 * gone. A typical level's difference is a few edited files and the git
 * objects of a commit or two, which is kilobytes.
 *
 * "Since the level was seeded" is a stamp file the seeder touches as the last
 * step of building the starting state — after the extraction and after the
 * per-learner salt commit — and `find -cnewer` against it. ctime and not
 * mtime, because ctime is the one a learner cannot set back (`touch -d`, `cp
 * -p` and `tar x` all set mtime) and because a `chmod` moves it too.
 *
 * ## Restoring is seeding plus the difference
 *
 * The seeder extracts the level exactly as it always does, applies the salt,
 * touches the stamp, and only then lays the difference over the top. So the
 * base the difference is applied to is the base it was taken against, which
 * is the one thing this depends on; the caller checks that with a fingerprint
 * of the seed script (`Mission.seedPrint`) before offering a restore at all.
 * Processes are not restored — a tarball holds files — but the level's boot
 * step runs after the restore, on the restored files, which is what a machine
 * does when it comes back up.
 *
 * Files the restore wrote may share the stamp's clock tick, and `-cnewer` is
 * a strict comparison, so the restore also writes down what it restored and
 * the next capture takes the union. Without that, a file restored in the same
 * tick would drop out of the next save and be lost on the reload after.
 *
 * Paths containing a newline are not kept: every list here is one path per
 * line. Nothing a level makes has one.
 */
/** Touched by the seeder once the starting state is complete. Everything newer is the learner's. */
export const SEEDED_STAMP = '/tmp/eos-seeded';
/** Paths a restore wrote, one per line, which the next capture counts as changed whatever their ctime. */
const RESTORED_FILES = '/tmp/eos-restored';
const RESTORED_DIRS = '/tmp/eos-restored-dirs';
/**
 * Paths the seeder asks every capture to keep whatever their ctime. The
 * salt's `update-ref` writes the branch's reflog with the time it ran, which
 * no seed can reproduce, so a restore that re-runs the salt would otherwise
 * give that one line a new date. Kept, the restore puts the learner's back.
 */
export const ALWAYS_KEEP = '/tmp/eos-always';
const CAPTURED = '/tmp/eos-save.tgz';
const INCOMING = '/tmp/eos-restore';
/** Forget the stamp and everything a restore wrote down: the workspace it described is gone. */
export const FORGET_STAMP = `rm -f ${SEEDED_STAMP} ${RESTORED_FILES} ${RESTORED_DIRS} ${ALWAYS_KEEP}`;
/**
 * The largest difference kept, compressed. Restoring goes in at about 20 KB/s
 * of payload, so this is a restore of up to half a minute; past it the level
 * is holding something that is not a learner's edits (a database the level
 * grew, a build output), and keeping a stale smaller save would bring back an
 * older state than the one they left, which is worse than none.
 */
export const MAX_SAVED_BYTES = 512 * 1024;
/**
 * Raw bytes per upload request. Sent as base64 text on stdin, which the
 * channel base64-encodes again for the wire: 24 KiB becomes about 44 KB on the
 * line, inside `MAX_REQUEST_BYTES` with room for the script.
 */
const UPLOAD_CHUNK_BYTES = 24 * 1024;
/** Raw bytes per read. 384 KiB is 512 KiB of base64, half the agent's 1 MiB stdout cap. */
const READ_CHUNK_BYTES = 384 * 1024;
/** The capture script. One exec: the lists, the archive and its size. */
function captureScript(missionId) {
    const tarball = q(`${LEVELS_DIR}/${missionId}.tar.gz`);
    return [
        'export LC_ALL=C',
        `cd ${WORKSPACE_ROOT} || exit 1`,
        // No stamp: the level is being seeded or reset right now, or never was.
        `[ -f ${SEEDED_STAMP} ] || exit 3`,
        // Files and symlinks: changed since the stamp, or restored, and still there.
        `{ find . -mindepth 1 \\( -type f -o -type l \\) -cnewer ${SEEDED_STAMP} -print; cat ${RESTORED_FILES} ${ALWAYS_KEEP} 2>/dev/null; } | sort -u |`,
        `  while IFS= read -r p; do if [ -L "$p" ] || [ -f "$p" ]; then printf '%s\\n' "$p"; fi; done > /tmp/eos-files`,
        // Directories the same way, each with its mode as it is now.
        `{ find . -mindepth 1 -type d -cnewer ${SEEDED_STAMP} -print; cat ${RESTORED_DIRS} 2>/dev/null; } | sort -u |`,
        `  while IFS= read -r p; do if [ -d "$p" ] && [ ! -L "$p" ]; then stat -c '%a %n' -- "$p"; fi; done > /tmp/eos-dirs`,
        // What the seed had that is gone. The tarball lists directories with a
        // trailing slash and the root as `./`, and find lists neither that way.
        // pipefail for this line alone, so a missing tarball is a failure and
        // not an empty list that would call nothing deleted.
        `set -o pipefail`,
        `tar tzf ${tarball} | sed -e 's#/$##' | grep -v -x -F -e . -e '' | sort -u > /tmp/eos-seedlist || exit 1`,
        `set +o pipefail`,
        `find . -mindepth 1 -print | sort -u > /tmp/eos-now`,
        `comm -23 /tmp/eos-seedlist /tmp/eos-now > /tmp/eos-deleted`,
        `if [ -s /tmp/eos-files ]; then tar czf ${CAPTURED} --no-recursion -T /tmp/eos-files || exit 4; else : > ${CAPTURED}; fi`,
        `wc -c < ${CAPTURED}`,
        `sha256sum ${CAPTURED} | cut -d' ' -f1`,
        `echo '--dirs'`,
        `cat /tmp/eos-dirs`,
        `echo '--deleted'`,
        `cat /tmp/eos-deleted`,
        `rm -f /tmp/eos-files /tmp/eos-dirs /tmp/eos-seedlist /tmp/eos-now /tmp/eos-deleted`,
    ].join('\n');
}
function failed(r, what) {
    return new Error(`${what} (exit ${r.code}): ${r.stderr.trim() || r.stdout.trim().slice(0, 200)}`);
}
function truncated(r) {
    return r.truncated === true;
}
function fromBase64(text) {
    const bin = atob(text);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++)
        out[i] = bin.charCodeAt(i);
    return out;
}
function toBase64(bytes) {
    let bin = '';
    for (let i = 0; i < bytes.length; i += 0x8000)
        bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    return btoa(bin);
}
/** Only `./`-relative paths with no `..` segment travel either way. */
function safePath(p) {
    return p.startsWith('./') && p.length > 2 && !p.split('/').includes('..');
}
/**
 * The learner's changes since the level was seeded, or why there are none to
 * keep. Several execs: the capture, then the archive in pieces the agent's
 * stdout cap can carry.
 */
export async function captureWork(ws, missionId) {
    const r = await ws.exec(captureScript(missionId), { timeoutMs: 60_000 });
    if (r.code === 3)
        return 'not-seeded';
    if (r.code !== 0 || truncated(r))
        throw failed(r, 'could not capture the workspace');
    const lines = r.stdout.split('\n');
    const size = Number(lines[0]);
    const sha256 = lines[1] ?? '';
    const dirsAt = lines.indexOf('--dirs');
    const deletedAt = lines.indexOf('--deleted');
    if (!Number.isInteger(size) || size < 0 || !/^[0-9a-f]{64}$/.test(sha256) || dirsAt !== 2 || deletedAt < dirsAt) {
        throw new Error('could not capture the workspace: the guest answered in an unexpected shape');
    }
    if (size > MAX_SAVED_BYTES)
        return 'too-large';
    const dirs = lines.slice(dirsAt + 1, deletedAt).filter((l) => /^[0-7]{3,4} /.test(l) && safePath(l.slice(l.indexOf(' ') + 1)));
    const deleted = lines.slice(deletedAt + 1).filter(safePath);
    const archive = new Uint8Array(size);
    for (let at = 0; at < size; at += READ_CHUNK_BYTES) {
        const n = Math.min(READ_CHUNK_BYTES, size - at);
        const chunk = await ws.exec(`tail -c +${at + 1} ${CAPTURED} | head -c ${n} | base64 -w0`, { timeoutMs: 60_000 });
        if (chunk.code !== 0 || truncated(chunk))
            throw failed(chunk, 'could not read the captured workspace');
        const bytes = fromBase64(chunk.stdout.trim());
        if (bytes.length !== n)
            throw new Error('could not read the captured workspace: a piece arrived short');
        archive.set(bytes, at);
    }
    if ((await sha256Hex(archive)) !== sha256)
        throw new Error('could not read the captured workspace: it arrived damaged');
    return { archive, dirs, deleted, sha256 };
}
async function sha256Hex(bytes) {
    const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', new Uint8Array(bytes)));
    return [...digest].map((b) => b.toString(16).padStart(2, '0')).join('');
}
/** Write bytes to a guest path, in pieces the line can carry. */
async function upload(ws, path, bytes) {
    if (bytes.length === 0) {
        const r = await ws.exec(`: > ${q(path)}`);
        if (r.code !== 0)
            throw failed(r, `could not write ${path}`);
        return;
    }
    for (let at = 0; at < bytes.length; at += UPLOAD_CHUNK_BYTES) {
        const r = await ws.exec(`base64 -d ${at === 0 ? '>' : '>>'} ${q(path)}`, {
            input: toBase64(bytes.subarray(at, at + UPLOAD_CHUNK_BYTES)),
            timeoutMs: 60_000,
        });
        if (r.code !== 0)
            throw failed(r, `could not write ${path}`);
    }
}
/**
 * Put saved work where `restoreScript` will look for it. Run before the
 * seeder's own exec, into /tmp, which the seeder does not wipe.
 */
export async function stageWork(ws, work) {
    const text = (lines) => new TextEncoder().encode(lines.map((l) => `${l}\n`).join(''));
    if (!/^[0-9a-f]{64}$/.test(work.sha256))
        throw new Error('saved work has no checksum');
    await upload(ws, `${INCOMING}.tgz`, work.archive);
    await upload(ws, `${INCOMING}.sum`, new TextEncoder().encode(`${work.sha256}  ${INCOMING}.tgz\n`));
    await upload(ws, `${INCOMING}.dirs`, text(work.dirs.filter((l) => /^[0-7]{3,4} /.test(l) && safePath(l.slice(l.indexOf(' ') + 1)))));
    await upload(ws, `${INCOMING}.deleted`, text(work.deleted.filter(safePath)));
}
/** Printed by the restore when it could not complete; the seeder then seeds again without it. */
export const RESTORE_FAILED = 'EOS_RESTORE_FAILED';
/**
 * The lines the seeder runs after the stamp, when there is staged work.
 *
 * Deletions first, then the archive, then directories deepest first so that
 * a directory the learner made read-only is closed after what is inside it
 * has been written. Each step writes down what it restored for the next
 * capture. A failure anywhere prints `RESTORE_FAILED` rather than failing the
 * exec: a level that will not open is worse than a level that opens fresh.
 *
 * Every step checks itself with `|| return 1`, and not with `set -e`: bash
 * ignores `set -e` inside anything whose status is being tested, and the
 * `|| echo` that reports the failure is exactly such a test. Written with
 * `set -e`, no step of this could fail.
 */
export function restoreScript() {
    return [
        'eos_restore() {',
        // Before anything is deleted: a damaged archive restores nothing.
        `  sha256sum -c --status ${INCOMING}.sum || return 1`,
        `  cd ${WORKSPACE_ROOT} || return 1`,
        `  while IFS= read -r p; do case "$p" in ./?*) rm -rf -- "$p" || return 1 ;; esac; done < ${INCOMING}.deleted`,
        `  if [ -s ${INCOMING}.tgz ]; then`,
        `    tar xzf ${INCOMING}.tgz -C ${WORKSPACE_ROOT} || return 1`,
        `    tar tzf ${INCOMING}.tgz > ${RESTORED_FILES} || return 1`,
        '  fi',
        // Deepest first by counting slashes, which a path with a space in it cannot confuse.
        `  awk -F/ '{ print NF "\\t" $0 }' ${INCOMING}.dirs | sort -rn | cut -f2- > ${INCOMING}.order || return 1`,
        `  while read -r mode p; do`,
        `    mkdir -p -- "$p" && chmod "$mode" -- "$p" || return 1`,
        `    printf '%s\\n' "$p" >> ${RESTORED_DIRS}`,
        `  done < ${INCOMING}.order`,
        '}',
        // A subshell, so the function's `cd` cannot move the lines after it.
        `( eos_restore ) || echo ${RESTORE_FAILED}`,
        `rm -f ${INCOMING}.tgz ${INCOMING}.sum ${INCOMING}.dirs ${INCOMING}.deleted ${INCOMING}.order`,
    ].join('\n');
}
//# sourceMappingURL=worksave.js.map